/* eslint-disable filenames/match-exported */
import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { Box, CircularProgress, Typography } from '@material-ui/core';
import WarningIcon from '@material-ui/icons/Warning';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import { PendenciasParametros } from './PendenciasParametros/PendenciasParametros';
import { INITIAL_FILTER_STATE, INITIAL_SORT_STATE } from './PendenciasTableConstants';
import { useAuth } from '../../../../auth/authProvider';
import partnershipRequest from '../../../../services/network/partnershipRequest';
import PendenciasParametrosTable from './PendenciasParametros/PendenciasParametrosTable';
import { PartnerDocumentsModal } from './PartnerDocumentsModal';

const SUMMARY_CARDS = [
  { key: 'pendencias', label: 'Pendências Ativas', Icon: WarningIcon, color: '#E65100', status: 'COM_PENDENCIA' },
  {
    key: 'aguardando',
    label: 'Aguardando Validação',
    Icon: AccessTimeIcon,
    color: '#1565C0',
    status: 'AGUARDANDO_VALIDACAO',
  },
  { key: 'aprovados', label: 'Aprovados', Icon: CheckCircleIcon, color: '#2E7D32', oracleRegistered: true },
  { key: 'rejeitados', label: 'Rejeitados', Icon: CancelIcon, color: '#C62828', status: 'RECUSADA' },
];

function SummaryCards({ summary, activeStatus, activeOracleRegistered, onFilter }) {
  return (
    <Box display="flex" gridGap={12} mb={2}>
      {SUMMARY_CARDS.map(({ key, label, Icon, color, status, oracleRegistered }) => {
        const isActive = oracleRegistered ? activeOracleRegistered : activeStatus === status;
        return (
          <Box
            key={key}
            flex={1}
            display="flex"
            alignItems="center"
            gridGap={12}
            p={2}
            onClick={() =>
              onFilter({
                status: isActive ? null : status,
                oracleRegistered: isActive ? null : oracleRegistered ?? null,
              })
            }
            style={{
              background: isActive ? `${color}12` : '#fff',
              border: `1px solid ${isActive ? color : 'rgba(0,0,0,0.10)'}`,
              borderRadius: 6,
              cursor: 'pointer',
              transition: 'border 0.15s, background 0.15s',
            }}
          >
            <Icon style={{ fontSize: 28, color }} />
            <Box>
              <Typography style={{ fontSize: '1.5rem', fontWeight: 700, lineHeight: 1.1, color }}>
                {summary[key] ?? 0}
              </Typography>
              <Typography
                style={{
                  fontSize: '0.7rem',
                  color: 'rgba(0,0,0,0.45)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                }}
              >
                {label}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

const Pendencias = () => {
  const { token } = useAuth();
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState(INITIAL_FILTER_STATE);
  const [sorts, setSorts] = useState(INITIAL_SORT_STATE);
  const [selectedPartner, setSelectedPartner] = useState(null);

  const buildSort = obj => {
    const [field, direction] = Object.entries(obj).find(([, v]) => v) || [];
    return field && direction ? `${field}:${direction}` : undefined;
  };

  const summaryParams = {
    parceiro: filters.parceiro?.length >= 3 ? filters.parceiro : undefined,
    createdAtFrom: filters.createdAtFrom || undefined,
  };

  const { data: summaryData } = useQuery(
    ['fetch-documentos-summary', summaryParams],
    () => partnershipRequest.get('partners/documents/summary', { token, params: summaryParams }),
    { staleTime: 120_000 },
  );
  const { data, isFetching } = useQuery(
    ['fetch-documentos', currentPage, rowsPerPage, filters, sorts],
    () => {
      const params = {
        sort: buildSort(sorts),
        page: currentPage,
        take: rowsPerPage,
        parceiro: filters.parceiro?.length >= 3 ? filters.parceiro : undefined,
        status: filters.status || undefined,
        oracleRegistered: filters.oracleRegistered || undefined,
      };
      return partnershipRequest.get('partners/documents', { token, params });
    },
    {
      staleTime: 120_000,
      keepPreviousData: true,
    },
  );

  const tableData = data?.content ?? [];
  const total = data?.total ?? 0;
  const summary = summaryData ?? { pendencias: 0, aguardando: 0, aprovados: 0, rejeitados: 0 };

  const blockingLoad = isFetching && data === undefined;

  const handleChangePage = (_, newPage) => setCurrentPage(newPage);

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(1);
  };

  const changeFilters = filter => {
    setCurrentPage(1);
    setFilters(filter);
  };

  const handleCardFilter = ({ status, oracleRegistered }) => {
    changeFilters({ ...filters, status: status || null, oracleRegistered: oracleRegistered || null });
  };

  const renderContent = () => {
    if (blockingLoad) {
      return (
        <div style={{ height: '60%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress />
        </div>
      );
    }

    if (tableData.length === 0) {
      return <Typography>Ops. Não encontramos nada.</Typography>;
    }

    return (
      <div style={{ opacity: isFetching ? 0.65 : 1, transition: 'opacity 0.2s' }}>
        <PendenciasParametrosTable
          data={tableData}
          setSorts={setSorts}
          sorts={sorts}
          total={total}
          rowsPerPage={rowsPerPage}
          currentPage={currentPage}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          onVerDetalhes={setSelectedPartner}
        />
      </div>
    );
  };

  return (
    <>
      <SummaryCards
        summary={summary}
        activeStatus={filters.status}
        activeOracleRegistered={filters.oracleRegistered}
        onFilter={handleCardFilter}
      />
      <PendenciasParametros filters={filters} setFilters={changeFilters} />
      {renderContent()}

      <PartnerDocumentsModal
        open={!!selectedPartner}
        partner={selectedPartner}
        onClose={() => setSelectedPartner(null)}
      />
    </>
  );
};

export { Pendencias };
